import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
import type { IMaterialBalanceCalculator, IMaterialBalanceInputRow, IMaterialBalanceResponse, ISimpleField, gasRateUnitId, pressureUnitId } from "../../interfaces/interfaces";
import { apiSlice } from "./apiSlice";
import { unitConverter } from "../../components/MeasurementUnits/UnitConverter";

export const MIN_MATERIAL_BALANCE_ROWS = 10;
export const MAX_MATERIAL_BALANCE_ROWS = 50;

const createTableField = (index: number, key: "p" | "q", value: number | null): ISimpleField => {
	return {
		id: `material-balance-input-${key}-${index}`,
		baseValue: value,
		currentValue: value,
		userValue: null,
		min: 0,
		max: key == "p" ? 600 : null
	};
};

const isEmptyField = (input: ISimpleField) => input.currentValue == null && !input.isIncomplete;

export const isEmptyRow = (row: IMaterialBalanceInputRow) => isEmptyField(row.q) && isEmptyField(row.p);

const initialState: IMaterialBalanceCalculator = {
	pressureAbandon: {
		id: 'material-balance-input-pressure',
		type: 'string',
		label: '',
		queryName: 'pressure_abandon',
		baseUnitId: 'bar',
		baseValue: 26,
		currentValue: 26,
		currentUnitId: 'bar',
		unitCategoryId: 'pressure',
		userValue: null,
		userUnitId: null,
		min: 0
	},
	inputTable: {
		0: {
			year: 2009,
			q: createTableField(0, "q", null),
			p: createTableField(0, "p", 109)
		},
		1: {
			year: 2010,
			q: createTableField(1, "q", 198),
			p: createTableField(1, "p", 109)
		},
		2: {
			year: 2011,
			q: createTableField(2, "q", 3234),
			p: createTableField(2, "p", 106)
		},
		3: {
			year: 2012,
			q: createTableField(3, "q", 3384),
			p: createTableField(3, "p", 103)
		},
		4: {
			year: 2013,
			q: createTableField(4, "q", 4564),
			p: createTableField(4, "p", 99)
		},
		5: {
			year: 2014,
			q: createTableField(5, "q", 4334),
			p: createTableField(5, "p", 94)
		},
		6: {
			year: 2015,
			q: createTableField(6, "q", 4388),
			p: createTableField(6, "p", 92)
		},
		7: {
			year: 2016,
			q: createTableField(7, "q", 4639),
			p: createTableField(7, "p", 88)
		},
		8: {
			year: 2017,
			q: createTableField(8, "q", 4847),
			p: createTableField(8, "p", 84)
		},
		9: {
			year: 2018,
			q: createTableField(9, "q", 4931),
			p: createTableField(9, "p", 80)
		},
		10: {
			year: 2019,
			q: createTableField(10, "q", 5060),
			p: createTableField(10, "p", 76)
		},
		11: {
			year: 2020,
			q: createTableField(11, "q", 4851),
			p: createTableField(11, "p", 68)
		},
		12: {
			year: 2021,
			q: createTableField(12, "q", 4931),
			p: createTableField(12, "p", 61)
		},
		13: {
			year: 2022,
			q: createTableField(13, "q", 4722),
			p: createTableField(13, "p", 55)
		},
		14: {
			year: 2023,
			q: createTableField(14, "q", 4189),
			p: createTableField(14, "p", 46)
		},
		15: {
			year: 2024,
			q: createTableField(15, "q", 3287),
			p: createTableField(15, "p", 41)
		},
		16: {
			year: 2025,
			q: createTableField(16, "q", 2365),
			p: createTableField(16, "p", 38)
		},
	},
	resultTable: {},
	resultTrend: {
		giip: null,
		g: null,
		rf: null,
		r: null,
		k: null,
		b: null
	},
	qMeasurementUnit: {
		baseUnitId: "thousand_m3_day",
		currentUnitId: null,
		unitList: ["mln_m3_day", "thousand_m3_day"]
	},
	pMeasurementUnit: {
		baseUnitId: "bar",
		currentUnitId: null,
		unitList: []
	},
	trendAjustmentFactor: 1,
};


const materialBalanceCalculatorSlice = createSlice({
	name: "materialBalanceCalculatorSlice",
	initialState,
	selectors: {
	},
	reducers: {
		setInputValue: (state, action: PayloadAction<{ index: number, key: "q" | "p" | "year", value: number | null, isIncomplete?: boolean }>) => {
			const { index, key, value, isIncomplete } = action.payload;

			if (key === "year") {
				state.inputTable[index].year = value;
				return;
			}

			const input = state.inputTable[index][key];
			if (!input) return;

			const baseUnitId = key === "q" ? state.qMeasurementUnit.baseUnitId : state.pMeasurementUnit.baseUnitId;
			const currentUnitId = (key === "q" ? state.qMeasurementUnit.currentUnitId : state.pMeasurementUnit.currentUnitId) ?? baseUnitId;

			input.currentValue = action.payload.value;
			input.baseValue = action.payload.value != null
				? unitConverter.convert(action.payload.value, currentUnitId, baseUnitId)
				: null;
			input.userValue = action.payload.value;
			if (isIncomplete) input.isIncomplete = true;
			else delete input.isIncomplete;

			state.resultTable = {};

			state.resultTrend.giip = null;
			state.resultTrend.g = null;
			state.resultTrend.rf = null;
			state.resultTrend.r = null;
			state.resultTrend.k = null;
			state.resultTrend.b = null;
		},
		setPressureAbandon: (state, action: PayloadAction<{ value: number | null, isIncomplete?: boolean }>) => {
			state.pressureAbandon.currentValue = action.payload.value;

			state.pressureAbandon.baseValue = (state.pressureAbandon.unitCategoryId && action.payload.value != null) ?
				unitConverter.convert(action.payload.value, state.pressureAbandon.currentUnitId, state.pressureAbandon.baseUnitId) :
				action.payload.value;

			state.pressureAbandon.userValue = action.payload.value;
			state.pressureAbandon.userUnitId = state.pressureAbandon.currentUnitId;
			if (action.payload.isIncomplete) state.pressureAbandon.isIncomplete = true;
			else delete state.pressureAbandon.isIncomplete;
		},
		setPressureAbandonUnit: (state, action: PayloadAction<{ toUnitId: pressureUnitId }>) => {
			const toUnitId = action.payload.toUnitId;
			if (!toUnitId) return;

			const input = state.pressureAbandon;
			input.currentValue = input.userValue != null && input.userUnitId != null
				? toUnitId === input.userUnitId
					? input.userValue
					: unitConverter.convert(input.userValue, input.userUnitId, toUnitId)
				: input.baseValue != null
					? unitConverter.convert(input.baseValue, input.baseUnitId, toUnitId)
					: null;
			input.currentUnitId = toUnitId;
		},
		setQMesurmentUnit: (state, action: PayloadAction<{ toUnitId: gasRateUnitId }>) => {
			const toUnitId = action.payload.toUnitId;
			if (!toUnitId) return;

			const baseUnitId = state.qMeasurementUnit.baseUnitId;

			state.qMeasurementUnit.currentUnitId = toUnitId;
			Object.values(state.inputTable).forEach(row => {
				const input = row.q;
				input.currentValue = input.baseValue != null
					? unitConverter.convert(input.baseValue, baseUnitId, toUnitId)
					: null;
			});
		},
		setPMesurmentUnit: (state, action: PayloadAction<{ toUnitId: pressureUnitId }>) => {
			const toUnitId = action.payload.toUnitId;
			if (!toUnitId) return;

			const baseUnitId = state.pMeasurementUnit.baseUnitId;

			state.pMeasurementUnit.currentUnitId = toUnitId;
			Object.values(state.inputTable).forEach(row => {
				const input = row.p;
				input.currentValue = input.baseValue != null
					? unitConverter.convert(input.baseValue, baseUnitId, toUnitId)
					: null;
			});
		},
		validateInputs: (state) => {
			const validateField = (input: ISimpleField, required: boolean) => {
				input.error = null;
				input.errorParams = null;

				if (input.isIncomplete) {
					input.error = "validation.invalid_number";
				} else if (input.min != null && input.currentValue != null && input.currentValue < input.min) {
					input.error = "validation.min_value_error";
					input.errorParams = { min: input.min };
				} else if (input.max != null && input.currentValue != null && input.currentValue > input.max) {
					input.error = "validation.max_value_error";
					input.errorParams = { max: input.max };
				} else if (required && input.currentValue == null) {
					input.error = "validation.is_required";
				} else if (required && input.currentValue === 0) {
					input.error = "validation.must_be_positive";
				}
			};

			validateField(state.pressureAbandon, true);

			const firstIndex = Math.min(...Object.keys(state.inputTable).map(Number));
			Object.entries(state.inputTable).forEach(([key, row]) => {
				const isFirst = Number(key) === firstIndex;
				if (!isFirst && isEmptyRow(row)) {
					validateField(row.q, false);
					validateField(row.p, false);
					return;
				}
				validateField(row.q, !isFirst);
				validateField(row.p, true);
			});
		},
		addRow: (state) => {
			if (Object.keys(state.inputTable).length >= MAX_MATERIAL_BALANCE_ROWS) return;

			const index = Math.max(-1, ...Object.keys(state.inputTable).map(Number)) + 1;
			state.inputTable[index] = {
				year: null,
				q: createTableField(index, "q", null),
				p: createTableField(index, "p", null)
			};
			state.resultTable = {};
		},
		removeRow: (state, action: PayloadAction<{ index: number }>) => {
			const indices = Object.keys(state.inputTable).map(Number);
			if (indices.length <= MIN_MATERIAL_BALANCE_ROWS || !indices.includes(action.payload.index)) return;

			delete state.inputTable[action.payload.index];
			state.resultTable = {};
		},
		insertValuesInARow: (state, action: PayloadAction<{ values: number[], startIndex: number }>) => {

		}
	},
	extraReducers: (builder) => {
		builder
			.addMatcher(apiSlice.endpoints.calculator.matchFulfilled, (state, action) => {
				const params = action.meta.arg.originalArgs as Record<string, string>;
				if (!params?.calculator_type) {
					console.warn("No calculator_type in calculator response");
					return;
				}
				if (params.calculator_type != "material_balance") {
					return;
				}

				const response = action.payload as IMaterialBalanceResponse;
				if (response?.results) {
					if (!response.results.gas_production_cumulative || !response.results.z_factor || !response.results.p_z) return;
					if (response.results.gas_production_cumulative.length != response.results.z_factor.length ||
						response.results.gas_production_cumulative.length != response.results.p_z.length ||
						response.results.z_factor.length != response.results.p_z.length) return;

					response.results.gas_production_cumulative.forEach((g, index) => {
						state.resultTable[index] = {
							g: g,
							z: response.results.z_factor[index],
							pz: response.results.p_z[index]
						};
					});

					state.resultTrend.giip = response.results.gas_initially_in_place;
					state.resultTrend.g = response.results.recoverable_reserves;
					state.resultTrend.rf = response.results.recovery_factor;
					state.resultTrend.r = response.results.r2;
					/* 
					{
						"results": {
							"gas_production_cumulative": [
								"198.0",
								"3432.0",
								"6816.0",
								"11380.0"
							],
							"z_factor": [
								"0.899",
								"0.901",
								"0.903",
								"0.906"
							],
							"p_z": [
								"121.2",
								"117.6",
								"114.0",
								"109.3"
							],
							"k": "-0.00107",
							"b": "121.36696",
							"gas_initially_in_place": "113701.10",
							"recoverable_reserves": "88673.18",
							"recovery_factor": "78.0",
							"r2": "0.99981"
						},
						"plots": [],
						"warnings": [],
						"errors": []
					}
					*/
				}
			})
	}
})

export default materialBalanceCalculatorSlice

export const {
	setInputValue,
	setPressureAbandon,
	setPressureAbandonUnit,
	setQMesurmentUnit,
	setPMesurmentUnit,
	validateInputs,
	insertValuesInARow,
	removeRow,
	addRow
} = materialBalanceCalculatorSlice.actions